import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import {
  initializeVoiceSocket,
  isVoiceSocketConnected
} from '../infrastructure/socket/initializeVoiceSocket';
import { DEFAULT_VOICE_SOCKET_EVENTS } from '../domain/voiceEventConstants';
import { voiceSetSocketConnected } from '../../../../actions/voice/voiceActions';
import { voiceSocketLog } from '../../../utils/voiceSocketDebug';
import useVoiceSocketPorts from './useVoiceSocketPorts';
import subscribeVoiceCallEvents from './socket/subscribeVoiceCallEvents';

export default function useVoiceSocket(options = {}) {
  const { events = DEFAULT_VOICE_SOCKET_EVENTS, enabled = true } = options;

  const retailerId = useSelector(
    (state) => state.retailerUsersReducer?.retailer_info?.id
  ) ?? (typeof ENV !== 'undefined' ? ENV.CURRENT_RETAILER_ID : null);
  const retailerUserId = useSelector(
    (state) => state.mainReducer?.currentRetailerUser?.id
  ) ?? (typeof ENV !== 'undefined' ? ENV.CURRENT_RETAILER_USER_ID : null);

  const ports = useVoiceSocketPorts(retailerId, retailerUserId);
  const portsRef = useRef(ports);
  portsRef.current = ports;

  const [isSocketConnected, setIsSocketConnected] = useState(isVoiceSocketConnected());

  const eventsKey = [...events].sort().join(',');

  useEffect(() => {
    if (!enabled) {
      voiceSocketLog('useVoiceSocket gated off (sip disabled) — skip connect');
      return undefined;
    }

    voiceSocketLog('useVoiceSocket mount', {
      events: eventsKey.split(','),
      retailerId,
      retailerUserId
    });

    const socket = initializeVoiceSocket({
      events: eventsKey.split(','),
      retailerId,
      retailerUserId
    });
    if (!socket) return undefined;

    const updateConnectionState = () => {
      const { connected } = socket;
      voiceSocketLog('connection state', { connected, socketId: socket.id });
      setIsSocketConnected(connected);
      portsRef.current.dispatch(voiceSetSocketConnected(connected));
    };

    socket.on('connect', updateConnectionState);
    socket.on('disconnect', updateConnectionState);
    updateConnectionState();

    const unsubscribers = subscribeVoiceCallEvents({
      dispatch: (...args) => portsRef.current.dispatch(...args),
      getHandlerState: () => portsRef.current.getHandlerState()
    });

    return () => {
      voiceSocketLog('useVoiceSocket cleanup');
      socket.off('connect', updateConnectionState);
      socket.off('disconnect', updateConnectionState);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [enabled, eventsKey, retailerId, retailerUserId]);

  const reconnect = useCallback(() => {
    voiceSocketLog('manual reconnect requested');
    initializeVoiceSocket({
      events: eventsKey.split(','),
      retailerId,
      retailerUserId
    })?.connect();
  }, [eventsKey, retailerId, retailerUserId]);

  return {
    isSocketConnected,
    reconnect
  };
}
